<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Clock Details" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            @click="handleExportToExcel"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-2 pb-3"
      >
        <v-col cols="12" sm="5" md="4" lg="3">
          <WeekPicker
            :hideDetails="true"
            :date="dateFilter"
            @weekChange="weekChangeHandler"
            @dateChange="(newValue) => this.dateFilter = newValue"
          ></WeekPicker>
        </v-col>
        <v-col cols="12" sm="6" md="3"
          v-if="clientsList && clientsList.length > 0"
          >
          <v-select
            label="Client"
            :items="clientsList"
            item-value="clientId"
            item-text="clientName"
            v-model="clientFilter"
            clearable
            outlined
            hide-details
          ></v-select>
        </v-col>
      </v-row>
    </div>
    <v-row class="my-0 py-0 hide-on-print">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12" class="px-4">
                <span class="headline">
                  <small>
                    Clock Out Was Not Recorded
                    <div class="red-square"></div>
                  </small>
                </span>
                <span class="headline ml-5">
                  <small>
                    Saved to Time Management
                    <div class="green-square"></div>
                  </small>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-container fluid class="px-0 pt-2">
      <v-card style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
        <v-card-text>
          <v-row class="hide-on-print">
            <v-col cols="12" align="right">
              <ToggleButton
                :state="collapseButtonState"
                color="primary"
                :statesLabel="collapseButtonLabels"
                @click="(state) => this.collapseButtonState = state === 1 ? 2 : 1 "
              ></ToggleButton>
            </v-col>
          </v-row>
          <v-data-table
            dense
            :headers="mainHeaders"
            :items="clockWeekLogsValue"
            item-key="clientId"
            :sort-by="['clientName']"
            :sort-desc="[false]"
            hide-default-footer
            show-expand
            :expanded="expanded"
            :items-per-page="-1"
          >
            <template v-slot:expanded-item="{ headers, item }">
              <td class="py-5" :colspan="headers.length">
                <v-data-table
                    dense
                    :headers="subHeaders"
                    :items="item.assignmentsLogs"
                    class="pb-3 px-5"
                >
                <template v-for="(slot,i) of subHeaders" v-slot:[`item.${slot.value}`]="{ item }">
                  <span :key="i">
                      <span
                        v-if="typeof item[`${slot.value}`] === 'object' &&
                        !Array.isArray(item[`${slot.value}`]) &&
                        item[`${slot.value}`].hasOwnProperty('hours')">
                          <v-chip
                            v-if="item[`${slot.value}`].state !== OK"
                            :color="getCellClass(item[`${slot.value}`].state)"
                            dark
                          >
                            {{ item[`${slot.value}`].hours }}
                          </v-chip>
                          <span v-else>
                            {{ item[`${slot.value}`].hours }}
                          </span>
                      </span>
                      <span :key="i" v-else>
                        {{ item[`${slot.value}`] }}
                      </span>
                  </span>
                </template>
                  <template v-slot:item.details="{ item }">
                    <v-btn
                      class="mx-1 my-1 hide-on-print"
                      color="primary"
                      outlined
                      @click="showLogDetails(item)"
                    >
                      <v-icon color="primary">info</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      <v-row justify="end" class="hide-on-sm">
        <v-col cols="5" class="mt-3">
          <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  block
                  depressed
                  color="primary"
                  @click="saveToTimeCard"
                >
                  Save To Time Management
                </v-btn>
              </template>
              <span>This action will upload only those rows that have not been uploaded yet and/or without any error</span>
            </v-tooltip>
        </v-col>
      </v-row>
      <div class="hide-on-md hide-on-print mt-5 abs-left-0">
        <v-container fluid class="fixed-buttons mt-0 px-0">
          <v-layout row>
            <v-flex xs12 md2 class="px-5 py-3">
              <v-btn
                block
                depressed
                color="primary"
                @click="saveToTimeCard"
              >
                Save To Time Management
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </div>
    </v-container>
    <BottomSpacer></BottomSpacer>
    <ClockLogsDetails
      ref="modalDetails"
      @timeUploadedToTimeCard="timeUploadedToTimeCardHandler"
    ></ClockLogsDetails>
    <MessageBox
      ref="messageBox"
      message="There is no time to save to Time Management"
      color="warning"
      closeTextColor="white"
    ></MessageBox>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer, ToggleButton, ButtonCustomIcon } from '@/components/buttons'
import { WeekPicker } from '@/components/shared/datePickers'
import ClockLogsDetails from './ClockLogsDetails'
import { OK, ERROR, UPLOADED_TO_TIME_SHEET_ENTRIES } from '@/lib/constants/clockLogStates'
import { MessageBox } from '@/components/shared/messages'
import excel from '@/lib/export/excel'
import { formatDate, toTimeOnlyString } from '@/lib/display'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    WeekPicker,
    ToggleButton,
    ClockLogsDetails,
    MessageBox,
    ButtonCustomIcon
  },
  data () {
    return {
      OK,
      ERROR,
      UPLOADED_TO_TIME_SHEET_ENTRIES,
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      mainHeaders: [
        { text: 'Client', value: 'clientName', sortable: false, cellClass: 'py-5' }
      ],
      collapseButtonLabels: {
        1: 'Contract All',
        2: 'Expand All'
      },
      collapseButtonState: 1, // 1 - Expanded, 2 - Collapsed
      clientsList: [],
      clientFilter: null
    }
  },
  computed: {
    ...mapFields('clockLogsReport', [
      'clockWeekLogs'
    ]),
    currentWeek () {
      return this.dateFilter >= new Date(this.dateBeginFilter) && this.dateFilter <= new Date(this.dateEndFilter)
    },
    subHeaders: {
      get () {
        let headers = [
          { text: 'Consultant', value: 'consultant', sortable: true }
        ]
        for (const item in this.clockWeekLogs[0].assignmentsLogs[0]) {
          headers.push({
            text: item,
            value: item,
            align: 'center',
            sortable: false
          })
        }
        headers.push({ text: 'Details', value: 'details', align: 'center', sortable: false })
        return headers.filter(
          header => header.text !== 'assignmentId' &&
            header.text !== 'consultant' &&
            header.text !== 'consultantUserId' &&
            header.text !== 'uploadedToTimeCard' &&
            header.text !== 'hasAnyError' &&
            header.text !== 'state' &&
            header.text !== 'weekLog'
        )
      }
    },
    expanded: {
      get () {
        if (this.collapseButtonState === 1) {
          return this.clockWeekLogsValue || []
        } else {
          return []
        }
      }
    },
    clockWeekLogsValue () {
      if (this.clientFilter) {
        return this.clockWeekLogs.filter(client => client.clientId === this.clientFilter)
      }
      return this.clockWeekLogs
    }
  },
  methods: {
    ...mapActions('clockLogsReport', [
      'getclockWeekLogs',
      'saveClockApprovedLogs'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime'
    ]),
    getCellClass (state) {
      switch (state) {
        case this.UPLOADED_TO_TIME_SHEET_ENTRIES:
          return 'green'
        case this.ERROR:
          return 'red'
        default:
          return null
      }
    },
    weekChangeHandler (dateBegin, dateEnd) {
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      this.getclockWeekLogs({ staffLynxBeginDate: dateBegin, staffLynxEndDate: dateEnd }).then((clockWeekLog) => {
        this.clientsList = clockWeekLog.map(client => {
          return {
            clientId: client.clientId,
            clientName: client.clientName
          }
        }).sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
      })
    },
    showLogDetails (item) {
      this.$refs.modalDetails.showModal(item.consultant, item.consultantUserId, JSON.parse(JSON.stringify(item.weekLog.clockDaysLog)))
    },
    timeUploadedToTimeCardHandler () {
      this.weekChangeHandler(this.dateBeginFilter, this.dateEndFilter)
    },
    saveToTimeCard () {
      let timeSheetEntriesToSave = []
      for (let client of this.clockWeekLogsValue) {
        for (let assignment of client.assignmentsLogs) {
          if (assignment.weekLog.clockDaysLog.some(
            day => day.state === this.ERROR ||
            day.state === this.UPLOADED_TO_TIME_SHEET_ENTRIES)) {
            continue
          }
          for (let dayLog of assignment.weekLog.clockDaysLog) {
            timeSheetEntriesToSave.push(
              {
                assignmentId: dayLog.assignmentId,
                userId: assignment.consultantUserId,
                date: dayLog.staffLynxLocalDateTime,
                hours: dayLog.workedTime
              }
            )
          }
        }
      }
      if (timeSheetEntriesToSave && timeSheetEntriesToSave.length > 0) {
        this.saveClockApprovedLogs({ approvedLogs: null, timesheetEntries: timeSheetEntriesToSave })
          .then(() => {
            this.weekChangeHandler(this.dateBeginFilter, this.dateEndFilter)
          })
      } else {
        this.$refs.messageBox.show()
      }
    },
    handleExportToExcel () {
      let dataToExport = {}
      // First Page: clients with their assignments and time
      let clientsPage = {
        headers: [['Client', 'Consultant']],
        rows: [],
        name: 'Clock Details'
      }
      for (let client of this.clockWeekLogsValue) {
        let row = {
          clientName: client.clientName
        }
        for (let assignment of client.assignmentsLogs) {
          row['consultantName'] = assignment.consultant
          for (let dayLog of assignment.weekLog.clockDaysLog) {
            row[formatDate(dayLog.staffLynxLocalDateTime)] = parseFloat(dayLog.workedTime).toFixed(2)
          }
        }
        clientsPage.rows.push(row)
      }
      dataToExport['clients'] = clientsPage

      // Add a page for each consultant with his details regarding clock in/out.
      for (let client of this.clockWeekLogsValue) {
        for (let assignment of client.assignmentsLogs) {
          let page = {
            headers: [['Action', 'Date', 'Day Of Week', 'Time', 'IP Address', 'Latitude', 'Longitude']],
            rows: [],
            name: assignment.consultant
          }
          for (let dayLog of assignment.weekLog.clockDaysLog) {
            for (let clockLog of dayLog.clockLogs) {
              let row = {
                action: clockLog.stateDescription,
                date: formatDate(clockLog.staffLynxLocalDateTime),
                dayOfWeek: dayLog.staffLynxLocalDayOfTheWeek,
                time: toTimeOnlyString(clockLog.staffLynxLocalDateTime),
                ipAddress: clockLog.ipAddress,
                latitude: clockLog.latitude,
                longitude: clockLog.longitude
              }
              page.rows.push(row)
            }
          }
          dataToExport[page.name] = page
        }
      }
      let params = {
        data: dataToExport,
        fileName: 'ClockDetails'
      }
      excel.exportObject2Excel(params)
    }
  }
}
</script>
